import { type UseFormReturn } from "react-hook-form";
import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import { LoginFormError } from "@/features/auth/components/LoginForm/Forms/FormParts/LoginFormError";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { FORGOT_PASSWORD_PATH } from "@/features/auth/routes";
import { type LoginFormData } from "@/features/auth/schemas/forms/loginForm.schema";
import { LoginPrivacyNoticeLink } from "@/features/legal/components/LoginPrivacyNoticeLink";
import { ButtonWithLoader } from "@/shared/components/customButtons/ButtonWithSpinner";
import { CaptchaField } from "@/shared/components/forms/CaptchaField";
import { ControlledTextInput } from "@/shared/components/forms/ControlledTextInput";
import { P } from "@/shared/ui/typographies/paragraph";

const HCAPTCHA_SITE_KEY = import.meta.env.VITE_HCAPTCHA_KEY;

type LoginFormProps = {
  form: UseFormReturn<LoginFormData>
  onSubmitForm: (data: LoginFormData) => Promise<void>
};

export const LoginForm = ({ form, onSubmitForm }: LoginFormProps) => {
  const {
    isTwoFactorRequired,
    isCellPhoneRequired,
    isFirstSignInRequired,
    isWaitingForUser,
    isWaitingForOrganization,
    isWaitingForPlan,
    isLegalComplianceRequired,
    isAuthenticated,
    isAuthenticating,
    error,
  } = useAuth();

  // Hide login form after step 1 during any intermediate loading or once authenticated
  if (
    isTwoFactorRequired ||
    isCellPhoneRequired ||
    isFirstSignInRequired ||
    isWaitingForUser ||
    isWaitingForOrganization ||
    isWaitingForPlan ||
    isLegalComplianceRequired ||
    isAuthenticated
  ) return null;

  return (
    <form
      data-testid="login-form"
      onSubmit={form.handleSubmit(onSubmitForm)}
      className="space-y-6 overflow-visible"
    >
      <LoginFormError title={error.title} description={error.description} />

      <ControlledTextInput<LoginFormData>
        id="email-input"
        label="Adresse e-mail"
        type="email"
        name="email"
        placeholder="votre@email.com"
        control={form.control}
        dataTestId="email-input"
        allowAutoComplete
        required
      />

      <ControlledTextInput<LoginFormData>
        id="password-input"
        label="Mot de passe"
        type="password"
        name="password"
        withPasswordEye
        control={form.control}
        dataTestId="password-input"
        allowAutoComplete
        required
        autoComplete="off"
      />

      {HCAPTCHA_SITE_KEY && (
        <CaptchaField
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={(token) => {
            form.clearErrors("hcaptcha");
            form.setValue("hcaptcha", token);
          }}
          errorMessage={form.formState.errors.hcaptcha?.message}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <Link
          to={FORGOT_PASSWORD_PATH}
          className="text-sm text-gray-500 hover:text-primary dark:text-gray-400"
          data-testid="forgot-password-link"
        >
          Mot de passe oublié ?
        </Link>
        <ButtonWithLoader
          type="submit"
          data-testid="login-submit-button"
          isLoading={isAuthenticating}
          disabled={form.formState.isSubmitting}
          color="primary"
          pill
        >
          <span className="flex items-center gap-2">
            {isAuthenticating ? "Connexion en cours..." : "Me connecter"}
            <ArrowRight size={16} aria-hidden />
          </span>
        </ButtonWithLoader>
      </div>
      <P className="text-muted-foreground text-xs text-pretty">
        En vous connectant, vous attestez avoir pris connaissance de la
        {" "}
        <LoginPrivacyNoticeLink />
        .
      </P>
    </form>
  );
};
